import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  Info,
  Minus,
  Phone,
  RefreshCw,
  Shield,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { HealthGauge, RiskLevel } from "../../components/ui/HealthGauge";
import { PageBanner } from "../../components/ui/PageBanner";
import {
  ActivityRisk,
  AggregatedHealthAdvice,
  getActivityRisks,
  getHealthAdvice,
  getHealthWarnings,
  HealthWarning,
} from "../../services/api/travelHealth";
import { getTravel, Travel } from "../../services/api/travels";
import { useAuthStore } from "../../stores/authStore";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import { useI18nStore } from "../../stores/i18nStore";
import {
  categoryLabel,
  riskLevelDescription,
  toDateOnly,
} from "../../utils/travelHealthLabels";

type Severity = "critical" | "high" | "medium" | "low";

interface SeverityStyle {
  bg: string;
  border: string;
  text: string;
  badge: string;
}

// Keyed by the concrete severity union rather than `string`, so
// `sevColor.low` is a guaranteed value and can serve as the fallback.
const sevColor: Record<Severity, SeverityStyle> = {
  critical: {
    bg: "bg-red-50",
    border: "border-red-300",
    text: "text-red-700",
    badge: "bg-red-100",
  },
  high: {
    bg: "bg-orange-50",
    border: "border-orange-200",
    text: "text-orange-700",
    badge: "bg-orange-100",
  },
  medium: {
    bg: "bg-yellow-50",
    border: "border-yellow-200",
    text: "text-yellow-700",
    badge: "bg-yellow-100",
  },
  low: {
    bg: "bg-green-50",
    border: "border-green-200",
    text: "text-green-700",
    badge: "bg-green-100",
  },
};

/** Severity arrives from the API as a plain string; unknown values read low. */
const styleFor = (severity: string): SeverityStyle =>
  severity in sevColor ? sevColor[severity as Severity] : sevColor.low;

const riskToGauge = (r: string): RiskLevel => {
  if (r === "critical" || r === "high") return "high";
  if (r === "medium") return "medium";
  return "low";
};

const categoryIcon: Record<string, React.ElementType> = {
  vaccination: Shield,
  medication: Activity,
  precaution: AlertTriangle,
  advisory: Info,
  warning: AlertTriangle,
};

const TrendIcon = ({ trend }: { trend: string }) => {
  if (trend === "increasing")
    return <TrendingUp className="h-4 w-4 text-red-500" />;
  if (trend === "decreasing")
    return <TrendingDown className="h-4 w-4 text-green-500" />;
  return <Minus className="h-4 w-4 text-gray-400" />;
};

export const RiskManagementPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const { user } = useAuthStore();
  const [travel, setTravel] = useState<Travel | null>(null);
  const [advice, setAdvice] = useState<AggregatedHealthAdvice | null>(null);
  const [warnings, setWarnings] = useState<HealthWarning[]>([]);
  const [activityRisks, setActivityRisks] = useState<ActivityRisk[]>([]);
  const [transportRisks, setTransportRisks] = useState<ActivityRisk[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingAdvice, setLoadingAdvice] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) loadTravel(id);
  }, [id]);

  const loadTravel = async (travelId: string) => {
    try {
      setLoading(true);
      const data = await getTravel(travelId);
      setTravel(data);
      await Promise.all([
        loadAdvice(data),
        // A trip without a destination country has no warnings to fetch;
        // the other two handle their own missing fields.
        data.country ? loadWarnings(data.country) : Promise.resolve(),
        loadActivityRisks(data),
      ]);
    } catch (err: any) {
      console.error("Failed to load travel:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadAdvice = useCallback(
    async (travelData: Travel) => {
      if (!travelData.country || !travelData.departureDate) return;
      try {
        setLoadingAdvice(true);
        setError(null);
        const data = await getHealthAdvice(
          travelData.country,
          toDateOnly(travelData.departureDate),
          toDateOnly(travelData.returnDate ?? travelData.departureDate),
          travelData.tripType ?? "flight",
          user?.country ?? undefined,
        );
        setAdvice(data);
      } catch (err: any) {
        setError(t.healthDataLoadError);
      } finally {
        setLoadingAdvice(false);
      }
    },
    [user?.country, t],
  );

  const loadWarnings = async (country: string) => {
    try {
      const data = await getHealthWarnings(country);
      setWarnings(data);
    } catch {
      // non-critical
    }
  };

  const loadActivityRisks = async (travelData: Travel) => {
    try {
      const activities: string[] = (travelData as any).activities ?? [];
      const travelType: string = travelData.tripType ?? "flight";
      if (activities.length === 0 && travelType === "other") return;
      const data = await getActivityRisks(travelType, activities);
      setTransportRisks(data.transportRisks);
      setActivityRisks(data.activityRisks);
    } catch {
      // non-critical
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  const riskLevel = advice?.riskLevel ?? "low";
  const outbreaks = advice?.activeOutbreaks ?? [];
  const recommendations = advice?.recommendations ?? [];

  // Group recommendations by category
  const byCategory = recommendations.reduce(
    (acc, r) => {
      const bucket = acc[r.category] ?? [];
      bucket.push(r);
      acc[r.category] = bucket;
      return acc;
    },
    {} as Record<string, typeof recommendations>,
  );

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/traveler/travels")}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <PageBanner
          icon={AlertTriangle}
          title={t.riskManagement ?? "Risk Management"}
          description={travel ? `${travel.destination}, ${travel.country}` : ""}
        />
      </div>

      {advice && (
        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-800">
          <div className="flex items-center gap-2">
            <Info className="h-4 w-4 shrink-0" />
            <span>
              {t.dataFrom}: {advice.sources?.map((s) => s.name).join(", ")} ·{" "}
              {fmt.date(advice.lastUpdated)}
            </span>
          </div>
          <button
            onClick={() =>
              travel?.country &&
              Promise.all([loadAdvice(travel), loadWarnings(travel.country)])
            }
            disabled={loadingAdvice}
            className="flex items-center gap-1 hover:text-blue-600"
          >
            <RefreshCw
              className={`h-4 w-4 ${loadingAdvice ? "animate-spin" : ""}`}
            />
          </button>
        </div>
      )}

      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3 text-sm text-yellow-800">
          {error}
        </div>
      )}

      {/* Overall Risk Gauge */}
      <div className="bg-white rounded-xl shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">
          {t.overallRisk}
        </h2>
        <div className="flex items-center justify-between gap-8 flex-wrap">
          <HealthGauge
            level={riskToGauge(riskLevel)}
            type="risk"
            size="lg"
            showLabel
          />
          <div className="flex-1 space-y-2 min-w-0">
            <div
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${styleFor(riskLevel).badge} ${styleFor(riskLevel).text}`}
            >
              {riskLevel.toUpperCase()}
            </div>
            <p className="text-sm text-gray-600">
              {riskLevelDescription(t, riskLevel)}
            </p>
            {outbreaks.length > 0 && (
              <p className="text-sm font-medium text-red-700">
                ⚠ {outbreaks.length} {t.activeOutbreaksDetected}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Active Outbreaks */}
      {outbreaks.length > 0 && (
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Activity className="h-5 w-5 text-red-600" />
            {t.activeOutbreaks}
            <span className="ml-auto text-sm font-normal text-gray-500">
              {outbreaks.length}
            </span>
          </h2>
          <div className="space-y-3">
            {outbreaks.map((o, i) => {
              const c = styleFor(o.severity);
              return (
                <div
                  key={i}
                  className={`p-4 rounded-lg border ${c.bg} ${c.border}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900">
                          {o.disease}
                        </span>
                        {o.region && (
                          <span className="text-sm text-gray-500">
                            ({o.region})
                          </span>
                        )}
                        <TrendIcon trend={o.trendDirection} />
                      </div>
                      <p className="text-sm text-gray-600">
                        {t.cases}: {fmt.number(o.casesReported, 0)} ·{" "}
                        {t.source ?? "Source"}: {o.source?.name}
                      </p>
                      <p className="text-xs text-gray-400">
                        {t.lastReported}: {fmt.date(o.lastReportDate)}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${c.badge} ${c.text}`}
                    >
                      {o.severity}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Travel Warnings */}
      {warnings.length > 0 && (
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-600" />
            {t.travelWarnings}
          </h2>
          <div className="space-y-3">
            {warnings.map((w, i) => {
              const c = styleFor(w.level);
              return (
                <div
                  key={i}
                  className={`p-4 rounded-lg border ${c.bg} ${c.border}`}
                >
                  <span className={`text-xs font-semibold uppercase ${c.text}`}>
                    {w.level}
                  </span>
                  <p className="text-sm text-gray-800 mt-1">{w.message}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Risk Factors by Category */}
      {Object.entries(byCategory).map(([category, recs]) => {
        const Icon = categoryIcon[category] ?? Shield;
        const maxSev = recs.reduce((max, r) => {
          const order = ["info", "low", "medium", "high", "critical"];
          return order.indexOf(r.severity) > order.indexOf(max)
            ? r.severity
            : max;
        }, "info");
        const c = styleFor(maxSev);
        return (
          <div key={category} className="bg-white rounded-xl shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Icon className={`h-5 w-5 ${c.text}`} />
              {categoryLabel(t, category)}
            </h2>
            <div className="space-y-3">
              {recs.map((r, i) => {
                const rc = styleFor(r.severity);
                return (
                  <div
                    key={i}
                    className={`p-4 rounded-lg border ${rc.bg} ${rc.border}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-medium text-gray-900">
                            {r.title}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs ${rc.badge} ${rc.text}`}
                          >
                            {r.severity}
                          </span>
                          <span className="text-xs text-gray-400">
                            {r.evidenceLevel}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{r.description}</p>
                        {r.details && (
                          <p className="text-xs text-gray-500 mt-1">
                            {r.details}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          {t.source ?? "Source"}: {r.source?.name}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Emergency */}
      <div className="bg-white rounded-xl shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Phone className="h-5 w-5 text-red-600" />
          {t.emergencyContacts ?? "Emergency Contacts"}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="font-medium text-gray-700">WHO Emergency</p>
            <p className="text-gray-600">+41 22 791 21 11</p>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="font-medium text-gray-700">
              CDC Emergency Operations
            </p>
            <p className="text-gray-600">+1 770-488-7100</p>
          </div>
        </div>
      </div>

      {/* AC 7: Transport Mode Risks */}
      {transportRisks.length > 0 && (
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-600" />
            {t.transportModeRisks}
            <span className="ml-auto text-xs font-normal text-gray-400 uppercase tracking-wide">
              {travel?.tripType ?? ""}
            </span>
          </h2>
          <div className="space-y-3">
            {transportRisks.map((r, i) => {
              const c = styleFor(r.severity);
              return (
                <div
                  key={i}
                  className={`p-4 rounded-lg border ${c.bg} ${c.border}`}
                >
                  <div className="flex items-start gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900 text-sm">
                          {r.title}
                        </span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.badge} ${c.text}`}
                        >
                          {r.severity}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        {r.description}
                      </p>
                      <ul className="space-y-1">
                        {r.recommendations.map((rec, ri) => (
                          <li
                            key={ri}
                            className="flex items-start gap-2 text-sm text-gray-700"
                          >
                            <span className="text-blue-500 shrink-0">•</span>
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* AC 8: Activity-Specific Risks */}
      {activityRisks.length > 0 && (
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Shield className="h-5 w-5 text-purple-600" />
            {t.activitySpecificRisks}
          </h2>
          <div className="space-y-3">
            {activityRisks.map((r, i) => {
              const c = styleFor(r.severity);
              return (
                <div
                  key={i}
                  className={`p-4 rounded-lg border ${c.bg} ${c.border}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-semibold text-gray-900 text-sm">
                          {r.title}
                        </span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.badge} ${c.text}`}
                        >
                          {r.severity}
                        </span>
                        <span className="text-xs text-gray-400 capitalize">
                          {r.activity}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        {r.description}
                      </p>
                      <ul className="space-y-1">
                        {r.recommendations.map((rec, ri) => (
                          <li
                            key={ri}
                            className="flex items-start gap-2 text-sm text-gray-700"
                          >
                            <span className="text-purple-500 shrink-0">•</span>
                            {rec}
                          </li>
                        ))}
                      </ul>
                      {r.requiredEquipment &&
                        r.requiredEquipment.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {r.requiredEquipment.map((eq, ei) => (
                              <span
                                key={ei}
                                className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full"
                              >
                                🎒 {eq}
                              </span>
                            ))}
                          </div>
                        )}
                      {r.requiredClearances &&
                        r.requiredClearances.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {r.requiredClearances.map((cl, ci) => (
                              <div
                                key={ci}
                                className="flex items-center gap-1 text-xs text-orange-700 bg-orange-50 px-2 py-1 rounded"
                              >
                                <AlertTriangle className="h-3 w-3 shrink-0" />
                                {cl}
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
